import type { ErrorCode, ProblemDetails, WebTokens } from '@emc/contracts';

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | 'network',
    message: string,
    readonly fields: Record<string, string> = {},
  ) {
    super(message);
  }
}

/**
 * El token de acceso vive solo en memoria. El refresh está en una cookie
 * httpOnly que el navegador envía a /api/v1/auth y el JavaScript no puede leer.
 */
let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const onExpire = new Set<() => void>();

export const session = {
  set: (t: WebTokens) => { accessToken = t.accessToken; },
  clear: () => { accessToken = null; },
  get active() { return !!accessToken; },
  onExpire: (fn: () => void) => { onExpire.add(fn); return () => { onExpire.delete(fn); }; },
};

const WEB = { 'x-emc-client': 'web' };

export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: 'POST', credentials: 'include', headers: { ...WEB, 'content-type': 'application/json' }, body: '{}',
      });
      if (!res.ok) { accessToken = null; return false; }
      session.set((await res.json()) as WebTokens);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

async function toError(res: Response) {
  const p = (await res.json().catch(() => null)) as ProblemDetails | null;
  const fields = Object.fromEntries((p?.errors ?? []).map((e) => [e.path, e.message]));
  return new ApiError(res.status, p?.code ?? 'validation_error', p?.detail ?? 'Ocurrió un error. Intentá de nuevo.', fields);
}

interface Options { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; form?: FormData }

async function raw(path: string, o: Options, retried = false): Promise<Response> {
  const isAuth = path.startsWith('/auth/');
  const headers: Record<string, string> = { accept: 'application/json', ...WEB };
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (o.body !== undefined) headers['content-type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API_URL}/api/v1${path}`, {
      method: o.method ?? (o.body !== undefined || o.form ? 'POST' : 'GET'),
      headers,
      credentials: isAuth ? 'include' : 'omit',
      body: o.form ?? (o.body !== undefined ? JSON.stringify(o.body) : undefined),
    });
  } catch {
    throw new ApiError(0, 'network', 'No se pudo conectar con el servidor. Revisá la conexión.');
  }
  if (res.status === 401 && !retried && !isAuth) {
    if (await refreshSession()) return raw(path, o, true);
    onExpire.forEach((fn) => fn());
  }
  if (!res.ok) throw await toError(res);
  return res;
}

export async function api<T>(path: string, o: Options = {}): Promise<T> {
  const res = await raw(path, o);
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/** Sube una imagen (la API la valida y la re-codifica). */
export function uploadImage(file: File, purpose: string) {
  const form = new FormData();
  form.append('file', file);
  return api<{ id: string; url: string; thumbUrl: string }>(`/media?purpose=${purpose}`, { form });
}

/** Descarga un archivo autenticado (ej. exportación CSV). */
export async function download(path: string) {
  const res = await raw(path, {});
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'descarga';
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.append(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const errorMessage = (e: unknown) => (e instanceof ApiError ? e.message : 'Ocurrió un error inesperado.');
